// @flow
import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk
} from '@reduxjs/toolkit';
import getBookingsReceivedRequest from '@justpark/api/src/requests/getBookingsReceived';
import getRequestError from '@justpark/api/src/helpers/getRequestError';
import transformIncludes from '@justpark/helpers/src/transformIncludes/transformIncludes';
import type { Pagination } from '../types';
import { BookingFilter } from '../types';

const sliceName = 'bookingsReceived';

type CustomInitialState = {
  loaded: boolean,
  isLoading: boolean,
  filter: BookingFilter,
  pagination: Pagination,
  error: string
};

const initialPagination = {
  count: null,
  currentPage: 1,
  perPage: null,
  total: null,
  totalPages: null,
  hasError: false
};

const customInitialState: CustomInitialState = {
  isLoading: false,
  filter: 'active',
  pagination: initialPagination,
  error: ''
};

export const selectBookingsReceivedIsLoading = (state) =>
  state[sliceName].isLoading;
export const selectBookingsReceivedCurrentPage = (state) =>
  state[sliceName].pagination.currentPage;
export const selectBookingsReceivedTotalPages = (state) =>
  state[sliceName].pagination.totalPages;
export const selectBookingsMadeFilter = (state) => state[sliceName].filter;
export const selectBookingsReceivedHasError = (state) =>
  state[sliceName].error !== '';
export const selectBookingsReceivedError = (state) => state[sliceName].error;
export const selectBookingsReceivedHasLoaded = (state) =>
  state[sliceName].loaded;

export const getBookingsReceived = createAsyncThunk(
  `${sliceName}/getBookingsReceived`,
  async (
    { page, filter },
    { extra: { jpApiClient }, rejectWithValue }
  ): any => {
    try {
      const statuses = filter === 'past' ? ['past', 'cancelled'] : [filter];
      const request = getBookingsReceivedRequest(page, statuses);
      const apiResponse = await jpApiClient(request);

      return transformIncludes(apiResponse.data, [
        'vehicle',
        'driver',
        'spaceOwnerEarnings'
      ]);
    } catch (err) {
      return rejectWithValue(getRequestError(err));
    }
  }
);

const bookingsReceivedAdapter = createEntityAdapter<BookingItem>();

const initialState = bookingsReceivedAdapter.getInitialState(
  customInitialState
);

const bookingsReceivedSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getBookingsReceived.pending, (state, action) => {
      const {
        meta: {
          arg: { page, filter }
        }
      } = action;

      const loadingNewPage =
        parseInt(page, 10) !== state.pagination.currentPage;

      const loadingNewFilter = filter !== state.filter;

      const newState = {
        ...state,
        isLoading: true,
        filter
      };

      if (loadingNewPage || loadingNewFilter) {
        return bookingsReceivedAdapter.removeAll({
          ...newState,
          loaded: false
        });
      }

      return newState;
    });
    builder.addCase(getBookingsReceived.fulfilled, (state, action) => {
      const { data: bookings, meta } = action.payload;
      const { pagination } = meta;

      return bookingsReceivedAdapter.setAll(
        {
          ...state,
          isLoading: false,
          loaded: true,
          error: '',
          pagination
        },
        bookings
      );
    });
    builder.addCase(getBookingsReceived.rejected, (state, action) => ({
      ...state,
      isLoading: false,
      loaded: false,
      error: action.payload
    }));
  }
});

export const { setFilter } = bookingsReceivedSlice.actions;

export const {
  selectAll: selectAllBookingsReceived
} = bookingsReceivedAdapter.getSelectors((state) => state[sliceName]);

export default bookingsReceivedSlice;
